import { Component, inject, OnInit, signal, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MaterialModule } from '../../../shared/material/material.module';
import { FormBuilder, Validators, FormArray } from '@angular/forms';
import { UserPreferencesCacheService } from '../../../shared/services/user-preferences-cache.service';
import { AvailabilityComponent } from '../../../shared/availability/availability.component';
import { CustomDate } from '../../../shared/services/custom-date.service';
import { AuthCustomService } from '../../../security/auth-custom.service';
import { firstValueFrom } from 'rxjs';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import { CurrencyMaskDirective } from '../../../shared/directives/currency-mask.directive';
import { ProviderPJContextService } from '../../../shared/services/provider-pj-context.service';
import { Address, Council, CouncilProfession, CouncilControllerService, ProfileProviderPJ, ProfileProviderPjControllerService, ServiceOffering, ServiceOfferingControllerService } from '../../../core/generated';
import { ContainerComponent } from '../../../shared/container/container.component';

// Register pt-BR locale for currency formatting
registerLocaleData(localePt);

@Component({
    selector: 'app-service-offering-provider-pj-form',
    standalone: true,
    imports: [
        CommonModule,
        MaterialModule,
        AvailabilityComponent,
        CurrencyMaskDirective,
        ContainerComponent
    ],
    templateUrl: './service-offering-provider-pj-form.component.html',
    styleUrls: ['./service-offering-provider-pj-form.component.scss']
})
export class ServiceOfferingProviderPJFormComponent
  implements OnInit, OnDestroy {
  serviceOfferingService = inject(ServiceOfferingControllerService);
  deviceService = inject(DeviceDetectorService);
  UserPreferencesCacheService = inject(UserPreferencesCacheService);
  councilService = inject(CouncilControllerService);
  router: Router = inject(Router);
  route: ActivatedRoute = inject(ActivatedRoute);
  fb = inject(FormBuilder);
  customDate = inject(CustomDate);
  profileProviderPJService = inject(ProfileProviderPjControllerService);
  authCustomService = inject(AuthCustomService);
  providerPJContextService = inject(ProviderPJContextService);
  serviceOffering: ServiceOffering[] = [];
  isMobile = this.deviceService.isMobile();
  readonly panelOpenState = signal(false);
  councils: Council[] = [];
  profilePJId: string | null = null;
  id: string | null = null;
  addresses: Address[] = [];
  providerPJ!: ProfileProviderPJ;
  private subscriptions: any[] = [];

  formServiceOffering = this.fb.group({
    id: [''],
    cnpj: [''],
    professionName: ['', Validators.required],
    specialty: ['', Validators.required],
    activityAreas: this.fb.array([]),
    adultCare: [false],
    pediatricCare: [false],
    serviceFormatOnLine: [false],
    serviceFormatPresential: [false],
    address: [null as Address | null],
    availability: this.fb.array([]),
    publicationDate: [''],
    expirationPublicationDate: [''],
    amount: [null as number | null],
    billingUnit: ['HOUR'],
    description: [''],
    status: ['DRAFT'],
    emergencyService: [false],
    emergencyServiceAvailability: this.fb.array([]),
  });

  professionsOptions: CouncilProfession[] = [];
  isLoadingProfessions = true;

  constructor() { }

  ngOnInit() {
    // Initialize FormArrays

    const days = [21, 22, 23, 31, 32, 33, 41, 42, 43, 51, 52, 53, 61, 62, 63, 71, 72, 73, 81, 82, 83];

    const availabilityArray = this.formServiceOffering.get(
      'availability'
    ) as FormArray;
    availabilityArray.clear();

    days.forEach((availability) => {
      availabilityArray.push(this.fb.control(availability));
    });


    const emergencyServiceAvailabilityArray = this.formServiceOffering.get(
      'emergencyServiceAvailability'
    ) as FormArray;
    emergencyServiceAvailabilityArray.clear();

    days.forEach((availability) => {
      emergencyServiceAvailabilityArray.push(this.fb.control(availability));
    });

    // Get profilePJId from route
    this.profilePJId =
      this.route.parent?.snapshot.paramMap.get('id') ??
      this.route.snapshot.paramMap.get('id') ??
      null;

    // Set CNPJ if we already have providerPJ data
    if (this.providerPJ) {
      this.formServiceOffering.get('cnpj')?.setValue(this.providerPJ.cnpj || null);
    }

    // Subscribe to context changes
    const subId = this.providerPJContextService.providerId$.subscribe((id) => {
      if (id !== null) this.profilePJId = id;
    });
    const subProfilePj = this.providerPJContextService.providerPJ$.subscribe(
      (c) => {
        if (c !== null && c !== this.providerPJ) {
          this.providerPJ = c;
          this.formServiceOffering.get('cnpj')?.setValue(c.cnpj || null);
        }
      }
    );
    this.subscriptions.push(subId, subProfilePj);

    const routeSnapshot = this.route.snapshot;
    if (routeSnapshot.paramMap.has('id')) {
      const idParam = routeSnapshot.paramMap.get('id');
      this.id = idParam ? idParam : null;
      if (this.id !== null) {
        this.loadServiceOffering(this.id);
      }
    }

    if (this.profilePJId) {
      this.loadCouncils(this.profilePJId);
      this.loadAddresses(this.profilePJId).then(() => {
        if (this.id) {
          this.loadServiceOffering(this.id);
        }
      });
    }
  }

  async loadAddresses(profileId: string): Promise<void> {
    const profileProviderId = parseInt(profileId, 10);
    return new Promise((resolve) => {
      const sub = this.profileProviderPJService.getAllAddresses
        (profileProviderId)
        .subscribe((addresses) => {
          this.addresses = addresses;
          resolve();
        });
      this.subscriptions.push(sub);
    });
  }

  getIdFromRoute(): string | null {
    let routeSnapshot = this.route.snapshot;

    // Verificar na rota atual
    if (routeSnapshot.paramMap.has('id')) {
      return routeSnapshot.paramMap.get('id');
    }

    // Verificar na rota pai
    if (routeSnapshot.parent && routeSnapshot.parent.paramMap.has('id')) {
      return routeSnapshot.parent.paramMap.get('id');
    }

    // Verificar na rota avô
    if (
      routeSnapshot.parent &&
      routeSnapshot.parent.parent &&
      routeSnapshot.parent.parent.paramMap.has('id')
    ) {
      return routeSnapshot.parent.parent.paramMap.get('id');
    }

    // Se não encontrou nada, tenta extrair da URL atual
    const urlMatch = this.router.url.match(/\/providerPJ\/([^\/]+)/);
    if (urlMatch) {
      return urlMatch[1];
    }

    return null;
  }

  loadServiceOffering(id: string) {
    const sub = this.serviceOfferingService
      .findById2(Number(id))
      .subscribe((serviceOffering) => {
        const serviceAddress = serviceOffering.address;
        const { address, ...serviceOfferingWithoutAddress } = serviceOffering;
        this.formServiceOffering.patchValue(
          serviceOfferingWithoutAddress as any
        );
        if (serviceAddress) {
          this.setAddressInForm(serviceAddress);
        }
        // Atualize manualmente o FormArray de availability
        const availabilityArray = this.formServiceOffering.get(
          'availability'
        ) as FormArray;
        availabilityArray.clear();
        if (serviceOffering.availability) {
          serviceOffering.availability.forEach((availability) => {
            availabilityArray.push(this.fb.control(availability));
          });
        }
        // Atualize manualmente o FormArray de emergencyServiceAvailability
        const emergencyServiceAvailabilityArray = this.formServiceOffering.get(
          'emergencyServiceAvailability'
        ) as FormArray;
        emergencyServiceAvailabilityArray.clear();
        if (serviceOffering.emergencyServiceAvailability) {
          serviceOffering.emergencyServiceAvailability.forEach(
            (availability) => {
              emergencyServiceAvailabilityArray.push(
                this.fb.control(availability)
              );
            }
          );
        }
      });
    this.subscriptions.push(sub);
  }

  private setAddressInForm(serviceAddress: Address): void {
    const matchedAddress = this.addresses.find(
      (addr) => addr.id === serviceAddress.id
    );
    if (matchedAddress) {
      this.formServiceOffering.get('address')?.setValue(matchedAddress);
    } else {
      this.addresses.push(serviceAddress);
      this.formServiceOffering.get('address')?.setValue(serviceAddress);
    }
    this.formServiceOffering.get('serviceFormatPresential')?.setValue(true);
  }

  async loadCouncils(id: string) {
    this.isLoadingProfessions = true;
    const profileProviderPJ = await firstValueFrom(
      this.profileProviderPJService.findById6(Number(id))
    );
    if (profileProviderPJ && profileProviderPJ.id) {
      const sub = this.councilService
        .findAllByProfileProviderPJId(profileProviderPJ.id)
        .subscribe({
          next: (councilList) => {
            councilList.forEach((council) => {
              if (council.councilProfession) {
                council.councilProfession.forEach(
                  (councilProfession: CouncilProfession) => {
                    if (councilProfession.certifiedHubMedical) {
                      this.professionsOptions.push(councilProfession!);
                    }
                  }
                );
              }
            });
          },
          complete: () => {
            this.isLoadingProfessions = false;
          },
        });
      this.subscriptions.push(sub);
    } else {
      this.isLoadingProfessions = false;
    }
  }

  async addServiceOffering() {
    // Check form validity first
    if (this.formServiceOffering.invalid) {
      this.formServiceOffering.markAllAsTouched();
      return;
    }

    const value = this.formServiceOffering.value;

    // Regra especial para status: se publicationDate estiver nulo/vazio, DRAFT, caso contrário OPEN
    const statusValue = (!value.publicationDate || value.publicationDate === '') 
      ? ServiceOffering.StatusEnum.Draft
      : ServiceOffering.StatusEnum.Open;

    const serviceOffering: ServiceOffering = {
      id: value.id ? Number(value.id) : undefined,
      cnpj: this.providerPJ?.cnpj ?? value.cnpj ?? '',
      professionName: value.professionName ?? '',
      specialty: value.specialty ?? '',
      activityAreas: this.preparTosaveActivityAreas(),
      adultCare: value.adultCare ?? false,
      pediatricCare: value.pediatricCare ?? false,
      serviceFormatOnLine: value.serviceFormatOnLine ?? false,
      serviceFormatPresential: value.serviceFormatPresential ?? false,
      address: value.address ?? undefined,
      availability: (value.availability as number[]) ?? [],
      publicationDate: value.publicationDate ?? '',
      expirationPublicationDate: value.expirationPublicationDate ?? '',
      amount: value.amount ?? undefined,
      billingUnit: value.billingUnit ?? 'HOUR',
      description: value.description ?? '',
      status: statusValue,
      emergencyService: value.emergencyService ?? false,
      emergencyServiceAvailability:
        (value.emergencyServiceAvailability as number[]) ?? [],
    };

    let sub;
    if (this.id) {
      sub = this.serviceOfferingService
        .update1(Number(this.id), serviceOffering)
        .subscribe({
          next: () => {
            this.router.navigate([
              '/providerPJ',
              this.profilePJId,
              'serviceOffering',
            ]);
          }
        });
    } else {
      sub = this.serviceOfferingService
        .save1(serviceOffering)
        .subscribe({
          next: () => {
            this.router.navigate([
              '/providerPJ',
              this.profilePJId,
              'serviceOffering',
            ]);
          }
        });
    }
    this.subscriptions.push(sub);
  }

  getProfessionName() {
    if (this.professionsOptions) {
      const tiposUnicos = [
        ...new Set(
          this.professionsOptions.map(
            (item) => item.professionInfo!.professionName
          )
        ),
      ];

      return tiposUnicos;
    }
    return [];
  }

  getSpecialty() {
    if (
      this.formServiceOffering.controls['professionName'].value &&
      this.professionsOptions
    ) {
      const professionName =
        this.formServiceOffering.controls['professionName']?.value;
      if (professionName && this.professionsOptions) {
        const councilProfessionFiter1 = this.professionsOptions.filter(
          (councilProfession) =>
            councilProfession.professionInfo &&
            councilProfession.professionInfo.professionName === professionName
        );
        const tiposUnicos = [
          ...new Set(
            councilProfessionFiter1
              .map((item) => item.professionInfo?.specialty)
              .filter((s) => !!s)
          ),
        ];
        return tiposUnicos;
      }
    }
    return [];
  }

  getActivityArea() {
    if (
      this.formServiceOffering.controls['specialty'].value &&
      this.professionsOptions
    ) {
      const councilProfessionFiter1 = this.professionsOptions.filter(
        (councilProfession) =>
          councilProfession.professionInfo!.professionName ===
          this.formServiceOffering.controls['professionName']?.value &&
          councilProfession.professionInfo!.specialty ===
          this.formServiceOffering.controls['specialty']?.value
      );
      const tiposUnicos = [
        ...new Set(
          councilProfessionFiter1.map(
            (item) => item.professionInfo!.activityAreas
          )
        ),
      ];

      return tiposUnicos[0];
    }
    return [];
  }

  preparTosaveActivityAreas(): string[] {
    let items = this.getActivityArea();
    if (items && items.length > 0) {
      return items
        .map((item) => item.activityArea)
        .filter((activityArea) => activityArea !== undefined) as string[];
    }
    return [];
  }

  get availability() {
    return this.formServiceOffering.get('availability') as FormArray;
  }

  get emergencyServiceAvailability() {
    return this.formServiceOffering.get(
      'emergencyServiceAvailability'
    ) as FormArray;
  }

  onAvailabilityChange(newAvailability: FormArray) {
    this.formServiceOffering.setControl('availability', newAvailability);
  }

  onEmergencyAvailabilityChange(newAvailability: FormArray) {
    this.formServiceOffering.setControl(
      'emergencyServiceAvailability',
      newAvailability
    );
  }

  get activityAreas() {
    return this.formServiceOffering.get('professionInfo.activityAreas') as any;
  }

  addActivityArea() {
    this.activityAreas.push(
      this.fb.group({
        activityArea: [''],
        activityAreaRQE: [''],
      })
    );
  }

  removeActivityArea(index: number) {
    this.activityAreas.removeAt(index);
  }

  dateFilter = (date: Date | null): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set the time to midnight to compare only dates
    const result = date ? date >= today : false;
    return result;
  };

  dateLimitFilter = (date: Date | null): boolean => {
    let publishDate = new Date();
    let today = new Date();

    if (this.formServiceOffering.controls.publicationDate.value) {
      publishDate = new Date(
        this.formServiceOffering.controls.publicationDate.value
      );
    } else {
      this.formServiceOffering.controls.expirationPublicationDate.reset();
    }

    if (this.formServiceOffering.controls.publicationDate.value) {
      today = new Date(this.formServiceOffering.controls.publicationDate.value);
    }
    today.setHours(0, 0, 0, 0); // Set the time to midnight to compare only dates
    const result = date ? date >= today : false;
    return result;
  };

  ngOnDestroy() {
    this.subscriptions.forEach(
      (sub) => sub && sub.unsubscribe && sub.unsubscribe()
    );
  }
}
